import { useMutation, useQueryClient } from '@tanstack/react-query';
import { FC } from 'react';
import styled, { css } from 'styled-components';

import useAlerts from '@/hooks/useAlerts';
import useHasPermission from '@/hooks/useHasPermission';
import useUser from '@/hooks/useUser';
import { InvoiceResource } from '@/types/resources';
import { api } from '@/utils/api';
import { getInitialsFromString } from '@/utils/helpers';

const CheckboxButton = styled.button<{ $approved: boolean }>`
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  width: 18px;
  height: 18px;
  padding: 0;
  border: 2px solid ${({ theme }) => theme.colors.success};
  border-radius: 3px;
  background: ${({ theme }) => theme.colors.white};
  color: ${({ theme }) => theme.colors.success};
  font-size: 9px;
  font-weight: 700;
  line-height: 1;
  letter-spacing: 0.5px;
  cursor: pointer;
  transition: background ease-out 0.2s;
  ${({ $approved, theme }) =>
    $approved &&
    css`
      background: ${theme.colors.success};
      color: ${theme.colors.white};
    `}

  &:disabled {
    cursor: not-allowed;
    opacity: 0.5;
  }
`;

interface Props {
  invoice: InvoiceResource;
  // Approval can only be toggled on the Received tab; elsewhere the checkbox
  // is shown read-only to reflect the approval state.
  interactive?: boolean;
}

const ApproveInvoiceCheckbox: FC<Props> = ({ invoice, interactive = true }) => {
  const canManageData = useHasPermission('manage invoices');
  const user = useUser();
  const alerts = useAlerts();
  const queryClient = useQueryClient();

  const isApproved = Boolean(invoice.approved_at);

  // An approved invoice can only be un-approved by the user who approved it.
  const canToggle = !isApproved || user?.id === invoice.approved_by_user_id;

  const { mutate, isLoading } = useMutation(
    () =>
      api.post(
        `/v1/payments/invoices/${invoice.id}/${
          isApproved ? 'unapprove' : 'approve'
        }`,
      ),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['invoices_list'], { exact: false });
      },
      onError: () => {
        alerts.error();
      },
    },
  );

  return (
    <CheckboxButton
      type="button"
      $approved={isApproved}
      aria-pressed={isApproved}
      aria-label={isApproved ? 'Un-approve invoice' : 'Approve invoice'}
      title={isApproved ? invoice.approved_by ?? undefined : undefined}
      disabled={!interactive || !canManageData || !canToggle || isLoading}
      onClick={() => mutate()}
    >
      {isApproved &&
        invoice.approved_by &&
        getInitialsFromString(invoice.approved_by)}
    </CheckboxButton>
  );
};

export default ApproveInvoiceCheckbox;
